import { formatNameFirstLast } from '@strata/helper-functions';
import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import { useNavigate, useParams } from 'react-router';

import { PasswordEntry } from 'components/PasswordEntry';
import { DeleteUserDialog } from 'pages/userManagement/dialogs/DeleteUserDialog';
import { CREATE_ROUTE, UserType } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { filterLighthouseRoles, handleDuplicateUserToast } from 'services/UserSupport';
import { getLighthouseUserSchema } from 'services/Validators';

export const LighthouseUserDetails = () => {
    const { upn } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(`/user-management/${UserType.LIGHTHOUSE}`);

    const isCreate = upn === CREATE_ROUTE;

    const user = useGet(!isCreate ? `/authorization/lighthouse_users/${upn}` : null);

    const roles = useGet('/authorization/roles/');
    const roleOptions = filterLighthouseRoles(roles);

    const isLoading = !(isCreate || user) || !roles;

    return (
        <Formik
            initialValues={{
                upn: user?.upn,
                full_name: user?.full_name,
                role: roleOptions?.find((role) => role.role_name === user?.role_name),
            }}
            enableReinitialize
            validationSchema={getLighthouseUserSchema(isCreate)}
            onSubmit={async (values) => {
                try {
                    let createResult;
                    const sharedValues = { role_name: values.role.role_name };

                    if (isCreate) {
                        createResult = await post('/authorization/lighthouse_users/', {
                            ...sharedValues,
                            upn: values.upn,
                            given_name: values.given_name,
                            family_name: values.family_name,
                            name: formatNameFirstLast(values.given_name, values.family_name),
                            password: values.password,
                        });
                    } else {
                        await put(`/authorization/lighthouse_users/${values.upn}`, {
                            ...sharedValues,
                            full_name: values.full_name,
                        });
                    }

                    if (createResult?.authzero_user_created === false) {
                        handleDuplicateUserToast(values.upn, UserType.LIGHTHOUSE);
                    } else {
                        SuccessToast({
                            message: `Lighthouse User: ${values.upn} ${
                                isCreate ? 'created' : 'updated'
                            } successfully.`,
                        });
                    }
                    goHome();
                } catch (err) {
                    handleError('Error saving user.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    isLoading={isLoading}
                    formTitle={`${isCreate ? 'Create' : 'Edit'} Lighthouse User`}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formHeaderRightContent={
                        !isCreate && (
                            <DeleteUserDialog
                                userType={UserType.LIGHTHOUSE}
                                onDeleteSuccess={goHome}
                            />
                        )
                    }
                    formLeftContent={
                        <>
                            <TextInputFormik
                                label="User Email"
                                id="upn"
                                disabled={!isCreate}
                                required
                            />
                            {isCreate ? (
                                <>
                                    <TextInputFormik label="First Name" id="given_name" required />
                                    <TextInputFormik label="Last Name" id="family_name" required />
                                    <PasswordEntry userType={UserType.LIGHTHOUSE} />
                                </>
                            ) : (
                                <TextInputFormik label="Full Name" id="full_name" required />
                            )}
                            <SuggestSingleSelectFormik
                                id="role"
                                label="Role"
                                required
                                values={roleOptions}
                                helperText={formik.values.role?.description}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
